import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './AdminMapCreator.css';


const AdminMapCreator = ({ standalone = true }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] = useState('casino');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  
  // Form states for different features
  const [casinoForm, setCasinoForm] = useState({
    name: '',
    cityName: '',
    countryName: '',
    slotMachines: 10,
    vaultMoney: 50000,
    securityLevel: 3
  });

  const [hotelForm, setHotelForm] = useState({
    name: '',
    country: '',
    city: '',
    totalRooms: 100,
    pricePerNight: 500,
    rating: 4,
    amenities: '',
    isCasino: false
  });

  // Star-based pricing system with random ranges
  const getStarBasedPrice = (rating, isCasino = false) => {
    const priceRanges = {
      1: { min: 50, max: 90 },   // 1-star: 50-90 WR
      2: { min: 100, max: 130 }, // 2-star: 100-130 WR
      3: { min: 140, max: 180 }, // 3-star: 140-180 WR
      4: { min: 190, max: 240 }, // 4-star: 190-240 WR
      5: { min: 250, max: 300 }  // 5-star: 250-300 WR
    };
    
    const range = priceRanges[rating] || priceRanges[3];
    const basePrice = Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;
    
    // Casino hotels get 20-50% bonus based on rating
    if (isCasino) {
      const casinoBonusPercents = {
        1: 0.20,  // Casino 1-star: +20% (60-108 WR)
        2: 0.25,  // Casino 2-star: +25% (125-162 WR)
        3: 0.30,  // Casino 3-star: +30% (182-234 WR)
        4: 0.40,  // Casino 4-star: +40% (266-336 WR)
        5: 0.50   // Casino 5-star: +50% (375-450 WR)
      };
      const bonus = Math.floor(basePrice * casinoBonusPercents[rating]);
      return basePrice + bonus;
    }
    
    return basePrice;
  };

  // Update price when rating or casino status changes
  const handleRatingChange = (newRating) => {
    const newPrice = getStarBasedPrice(newRating, hotelForm.isCasino);
    setHotelForm({
      ...hotelForm,
      rating: newRating,
      pricePerNight: newPrice
    });
  };

  const handleCasinoChange = (isCasino) => {
    const newPrice = getStarBasedPrice(hotelForm.rating, isCasino);
    setHotelForm({
      ...hotelForm,
      isCasino: isCasino,
      pricePerNight: newPrice
    });
  };

  const [bankForm, setBankForm] = useState({
    bankName: '',
    cityName: '',
    countryName: '',
    vaultBalance: 1000000,
    securityLevel: 5
  });


  const [atmForm, setAtmForm] = useState({
    bankName: '',
    atmCode: ''
  });

  // Coordinate states
  const [coordinates, setCoordinates] = useState({
    lat: '',
    lng: ''
  });

  // Created feature state
  const [createdFeature, setCreatedFeature] = useState(null);
  const [isReadyToPlace, setIsReadyToPlace] = useState(false);

  // Component initialization
  useEffect(() => {
    console.log('AdminMapCreator initialized');
  }, []);

  const resetAll = () => {
    setCreatedFeature(null);
    setIsReadyToPlace(false);
    setMessage('');
    setCoordinates({ lat: '', lng: '' });
  };

  const pasteCoordinates = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const coordMatch = text.match(/(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
      if (coordMatch) {
        setCoordinates({
          lat: coordMatch[1],
          lng: coordMatch[2]
        });
        setMessage('Coordinates pasted successfully!');
      } else {
        setMessage('Invalid coordinate format. Expected: "lat, lng"');
      }
    } catch (error) {
      setMessage('Failed to paste coordinates. Please paste manually.');
    }
  };

  const startPlacement = () => {
    // Navigate to Hack the Planet page where admin can place the created feature
    navigate('/hack-the-planet');
  };

  const createFeature = async () => {
    // Validate form data
    let isValid = true;
    let validationMessage = '';

    switch (activeFeature) {
      case 'casino':
        if (!casinoForm.name || !casinoForm.cityName || !casinoForm.countryName) {
          isValid = false;
          validationMessage = 'Please fill in casino name, city, and country';
        }
        break;
      case 'hotel':
        if (!hotelForm.name || !hotelForm.country) {
          isValid = false;
          validationMessage = 'Please fill in hotel name and country';
        }
        break;
      case 'bank':
        if (!bankForm.bankName || !bankForm.cityName || !bankForm.countryName) {
          isValid = false;
          validationMessage = 'Please fill in bank name, city, and country';
        }
        break;
      case 'atm':
        // ATM has no required fields
        break;
      default:
        isValid = false;
        validationMessage = 'Invalid feature type';
    }

    // Validate coordinates
    if (!coordinates.lat || !coordinates.lng) {
      setMessage('Please provide valid coordinates (latitude and longitude)');
      return;
    }

    const lat = parseFloat(coordinates.lat);
    const lng = parseFloat(coordinates.lng);

    if (isNaN(lat) || isNaN(lng)) {
      setMessage('Coordinates must be valid numbers');
      return;
    }

    if (lat < -90 || lat > 90) {
      setMessage('Latitude must be between -90 and 90');
      return;
    }

    if (lng < -180 || lng > 180) {
      setMessage('Longitude must be between -180 and 180');
      return;
    }

    if (!isValid) {
      setMessage(validationMessage);
      return;
    }

    setLoading(true);
    try {
      // Create feature object with admin-specified coordinates
      let featureData = {
        coordinates: {
          lat: parseFloat(coordinates.lat),
          lng: parseFloat(coordinates.lng)
        }
      };
      
      switch (activeFeature) {
        case 'casino':
          featureData = {
            ...featureData,
            ...casinoForm
          };
          break;
        case 'hotel':
          featureData = {
            ...featureData,
            ...hotelForm,
            amenities: hotelForm.amenities.split(',').map(a => a.trim()).filter(a => a)
          };
          break;
        case 'bank':
          featureData = {
            ...featureData,
            ...bankForm
          };
          break;
        case 'atm':
          featureData = {
            ...featureData,
            ...atmForm,
            atmCode: atmForm.atmCode || Math.floor(1000 + Math.random() * 9000).toString()
          };
          break;
      }

      // Create feature directly in database
      console.log(`Creating ${activeFeature} with data:`, featureData);
      console.log('AdminMapCreator debug: request initiated');
      
      // Create feature using API service (CSRF token handled automatically)
      const response = await api.post(`/admin/create-${activeFeature}`, featureData);
      const data = response.data;
      
      if (data.success) {
        const featureWithType = { 
          ...(data.feature || data), 
          type: activeFeature // Ensure type is always set
        };
        setCreatedFeature(featureWithType);
        setIsReadyToPlace(true);
        setMessage(`${activeFeature.charAt(0).toUpperCase() + activeFeature.slice(1)} created successfully! Click "Go to Hack the Planet" to place it on the map.`);
      } else {
        setMessage(`Error: ${data.message || 'Failed to create feature'}`);
      }
    } catch (error) {
      console.error('Error creating feature:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      // Extract meaningful error message
      let errorMessage = 'Network error occurred while creating feature';
      
      if (error.response) {
        // Server responded with error status
        console.log('Response status:', error.response.status);
        console.log('Response data:', error.response.data);
        
        if (error.response.status === 400) {
          errorMessage = error.response.data?.message || 'Invalid data provided. Please check all required fields and coordinate values.';
        } else if (error.response.status === 401) {
          errorMessage = 'Authentication required. Please log in as admin.';
        } else if (error.response.status === 403) {
          errorMessage = 'Admin access required.';
        } else if (error.response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = `Server error (${error.response.status}): ${error.response.data?.message || 'Unknown error'}`;
        }
      } else if (error.request) {
        // Network error - no response received
        errorMessage = 'Network connection failed. Please check your internet connection and try again.';
      } else {
        // Other error
        errorMessage = `Error: ${error.message}`;
      }
      
      setMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };


  const renderCreatedFeature = () => {
    if (!createdFeature || !createdFeature.type) return null;

    return (
      <div className="created-feature-display" style={{
        marginTop: '20px',
        padding: '15px',
        border: '2px solid #00ff41',
        borderRadius: '8px',
        backgroundColor: 'rgba(0, 255, 65, 0.1)'
      }}>
        <h4 style={{ color: '#00ff41', marginBottom: '10px' }}>
          ✅ {createdFeature.type.charAt(0).toUpperCase() + createdFeature.type.slice(1)} Created
        </h4>
        
        {createdFeature.type === 'casino' && (
          <div>
            <p><strong>Name:</strong> {createdFeature.name}</p>
            <p><strong>Location:</strong> {createdFeature.cityName}, {createdFeature.countryName}</p>
            <p><strong>Slot Machines:</strong> {createdFeature.slotMachines}</p>
            <p><strong>Vault Money:</strong> {createdFeature.vaultMoney?.toLocaleString()} WR</p>
            <p><strong>Security Level:</strong> {createdFeature.securityLevel}</p>
          </div>
        )}
        
        {createdFeature.type === 'hotel' && (
          <div>
            <p><strong>Name:</strong> {createdFeature.name}</p>
            <p><strong>Location:</strong> {createdFeature.country}{createdFeature.city ? `, ${createdFeature.city}` : ''}</p>
            <p><strong>Rooms:</strong> {createdFeature.totalRooms}</p>
            <p><strong>Price:</strong> {createdFeature.pricePerNight} WR/night</p>
            <p><strong>Rating:</strong> {'★'.repeat(createdFeature.rating)}</p>
            {createdFeature.amenities?.length > 0 && (
              <p><strong>Amenities:</strong> {createdFeature.amenities.join(', ')}</p>
            )}
            <p><strong>Casino Hotel:</strong> {createdFeature.isCasino ? 'Yes' : 'No'}</p>
          </div>
        )}
        
        {createdFeature.type === 'bank' && (
          <div>
            <p><strong>Name:</strong> {createdFeature.bankName}</p>
            <p><strong>Location:</strong> {createdFeature.cityName}, {createdFeature.countryName}</p>
            <p><strong>Vault Balance:</strong> {createdFeature.vaultBalance?.toLocaleString()} WR</p>
            <p><strong>Security Level:</strong> {createdFeature.securityLevel}</p>
          </div>
        )}
        
        
        {createdFeature.type === 'atm' && (
          <div>
            <p><strong>Bank:</strong> {createdFeature.bankName || 'Occult Bank'}</p>
            <p><strong>ATM Code:</strong> {createdFeature.atmCode}</p>
          </div>
        )}
        
        <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
          <button
            onClick={startPlacement}
            style={{
              padding: '10px 20px',
              backgroundColor: '#ff8800',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              flex: 1
            }}
          >
🌍 Go to Hack the Planet
          </button>
          <button
            onClick={resetAll}
            style={{
              padding: '10px 15px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            🗑️ Reset
          </button>
        </div>
      </div>
    );
  };

  const renderCoordinateInputs = () => {
    return (
      <div style={{ marginTop: '20px', padding: '15px', border: '2px solid #00ff41', borderRadius: '8px', backgroundColor: 'rgba(0, 255, 65, 0.1)' }}>
        <h4 style={{ color: '#00ff41', marginBottom: '10px' }}>📍 Feature Coordinates</h4>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '5px', color: '#ccc' }}>Latitude:</label>
            <input
              type="number"
              step="any"
              placeholder="e.g. 40.7128"
              value={coordinates.lat}
              onChange={(e) => setCoordinates({...coordinates, lat: e.target.value})}
              className="form-input"
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '5px', color: '#ccc' }}>Longitude:</label>
            <input
              type="number"
              step="any"
              placeholder="e.g. -74.0060"
              value={coordinates.lng}
              onChange={(e) => setCoordinates({...coordinates, lng: e.target.value})}
              className="form-input"
              style={{ width: '100%' }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            onClick={pasteCoordinates}
            style={{
              padding: '8px 12px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            📋 Paste Coordinates
          </button>
          <span style={{ fontSize: '12px', color: '#888' }}>
            Get coordinates from "Hack the Planet" coordinate picker
          </span>
        </div>
      </div>
    );
  };

  const renderForm = () => {
    switch (activeFeature) {
      case 'casino':
        return (
          <div className="feature-form">
            <h3>🎰 Create Casino</h3>
            <input
              type="text"
              placeholder="Casino Name"
              value={casinoForm.name}
              onChange={(e) => setCasinoForm({...casinoForm, name: e.target.value})}
              className="form-input"
            />
            <input
              type="text"
              placeholder="City Name"
              value={casinoForm.cityName}
              onChange={(e) => setCasinoForm({...casinoForm, cityName: e.target.value})}
              className="form-input"
            />
            <input
              type="text"
              placeholder="Country Name"
              value={casinoForm.countryName}
              onChange={(e) => setCasinoForm({...casinoForm, countryName: e.target.value})}
              className="form-input"
            />
            <input
              type="number"
              placeholder="Slot Machines"
              value={casinoForm.slotMachines}
              onChange={(e) => setCasinoForm({...casinoForm, slotMachines: parseInt(e.target.value)})}
              className="form-input"
            />
            <input
              type="number"
              placeholder="Vault Money"
              value={casinoForm.vaultMoney}
              onChange={(e) => setCasinoForm({...casinoForm, vaultMoney: parseInt(e.target.value)})}
              className="form-input"
            />
            <select
              value={casinoForm.securityLevel}
              onChange={(e) => setCasinoForm({...casinoForm, securityLevel: parseInt(e.target.value)})}
              className="form-input"
            >
              <option value={1}>Security Level 1</option>
              <option value={2}>Security Level 2</option>
              <option value={3}>Security Level 3</option>
              <option value={4}>Security Level 4</option>
              <option value={5}>Security Level 5</option>
            </select>
            {renderCoordinateInputs()}
          </div>
        );
      case 'hotel':
        return (
          <div className="feature-form">
            <h3>🏨 Create Hotel</h3>
            <input
              type="text"
              placeholder="Hotel Name"
              value={hotelForm.name}
              onChange={(e) => setHotelForm({...hotelForm, name: e.target.value})}
              className="form-input"
            />
            <input
              type="text"
              placeholder="Country"
              value={hotelForm.country}
              onChange={(e) => setHotelForm({...hotelForm, country: e.target.value})}
              className="form-input"
            />
            <input
              type="text"
              placeholder="City"
              value={hotelForm.city}
              onChange={(e) => setHotelForm({...hotelForm, city: e.target.value})}
              className="form-input"
            />
            <input
              type="number"
              placeholder="Total Rooms"
              value={hotelForm.totalRooms}
              onChange={(e) => setHotelForm({...hotelForm, totalRooms: parseInt(e.target.value)})}
              className="form-input"
            />
            <div className="form-input-group">
              <label className="form-label">Price Per Night (Auto-calculated)</label>
              <input
                type="number"
                placeholder="Price Per Night"
                value={hotelForm.pricePerNight}
                readOnly
                className="form-input readonly-input"
                title="Price is automatically calculated based on star rating and casino status"
              />
              <small className="price-info">💰 Random price within star rating range {hotelForm.isCasino ? '+ casino percentage bonus' : ''}</small>
            </div>
            <div className="form-input-group">
              <label className="form-label">Star Rating</label>
              <select
                value={hotelForm.rating}
                onChange={(e) => handleRatingChange(parseInt(e.target.value))}
                className="form-input"
              >
                <option value={1}>⭐ 1 Star (50-90 WR{hotelForm.isCasino ? ' +20% casino bonus' : ''})</option>
                <option value={2}>⭐⭐ 2 Stars (100-130 WR{hotelForm.isCasino ? ' +25% casino bonus' : ''})</option>
                <option value={3}>⭐⭐⭐ 3 Stars (140-180 WR{hotelForm.isCasino ? ' +30% casino bonus' : ''})</option>
                <option value={4}>⭐⭐⭐⭐ 4 Stars (190-240 WR{hotelForm.isCasino ? ' +40% casino bonus' : ''})</option>
                <option value={5}>⭐⭐⭐⭐⭐ 5 Stars (250-300 WR{hotelForm.isCasino ? ' +50% casino bonus' : ''})</option>
              </select>
            </div>
            <input
              type="text"
              placeholder="Amenities (comma-separated)"
              value={hotelForm.amenities}
              onChange={(e) => setHotelForm({...hotelForm, amenities: e.target.value})}
              className="form-input"
            />
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={hotelForm.isCasino}
                onChange={(e) => handleCasinoChange(e.target.checked)}
              />
              🎰 This hotel is also a casino (+20-50% pricing bonus)
            </label>
            {renderCoordinateInputs()}
          </div>
        );
      case 'bank':
        return (
          <div className="feature-form">
            <h3>🏦 Create Bank</h3>
            <input
              type="text"
              placeholder="Bank Name"
              value={bankForm.bankName}
              onChange={(e) => setBankForm({...bankForm, bankName: e.target.value})}
              className="form-input"
            />
            <input
              type="text"
              placeholder="City Name"
              value={bankForm.cityName}
              onChange={(e) => setBankForm({...bankForm, cityName: e.target.value})}
              className="form-input"
            />
            <input
              type="text"
              placeholder="Country Name"
              value={bankForm.countryName}
              onChange={(e) => setBankForm({...bankForm, countryName: e.target.value})}
              className="form-input"
            />
            <input
              type="number"
              placeholder="Vault Balance"
              value={bankForm.vaultBalance}
              onChange={(e) => setBankForm({...bankForm, vaultBalance: parseInt(e.target.value)})}
              className="form-input"
            />
            <select
              value={bankForm.securityLevel}
              onChange={(e) => setBankForm({...bankForm, securityLevel: parseInt(e.target.value)})}
              className="form-input"
            >
              <option value={1}>Security Level 1</option>
              <option value={2}>Security Level 2</option>
              <option value={3}>Security Level 3</option>
              <option value={4}>Security Level 4</option>
              <option value={5}>Security Level 5</option>
            </select>
            {renderCoordinateInputs()}
          </div>
        );
      case 'atm':
        return (
          <div className="feature-form">
            <h3>🏧 Create ATM</h3>
            <input
              type="text"
              placeholder="Bank Name (optional)"
              value={atmForm.bankName}
              onChange={(e) => setAtmForm({...atmForm, bankName: e.target.value})}
              className="form-input"
            />
            <input
              type="text"
              placeholder="ATM Code (optional - auto-generated if empty)"
              value={atmForm.atmCode}
              onChange={(e) => setAtmForm({...atmForm, atmCode: e.target.value})}
              className="form-input"
              maxLength="4"
            />
            <p style={{ fontSize: '12px', color: '#888', margin: '10px 0' }}>
              Leave ATM Code empty to auto-generate a 4-digit code
            </p>
            {renderCoordinateInputs()}
          </div>
        );
      default:
        return null;
    }
  };

  if (!standalone) {
    return (
      <div className="admin-map-creator">
        <div className="admin-header">
          <h2>🏗️ Map Feature Creator</h2>
          <p>Create and place new map features anywhere in the world</p>
        </div>

        <div className="feature-selector">
          <button
            className={`feature-button ${activeFeature === 'casino' ? 'active' : ''}`}
            onClick={() => setActiveFeature('casino')}
          >
            🎰 Casino
          </button>
          <button
            className={`feature-button ${activeFeature === 'hotel' ? 'active' : ''}`}
            onClick={() => setActiveFeature('hotel')}
          >
            🏨 Hotel
          </button>
          <button
            className={`feature-button ${activeFeature === 'bank' ? 'active' : ''}`}
            onClick={() => setActiveFeature('bank')}
          >
            🏦 Bank
          </button>
          <button
            className={`feature-button ${activeFeature === 'atm' ? 'active' : ''}`}
            onClick={() => setActiveFeature('atm')}
          >
            🏧 ATM
          </button>
        </div>

        <div className="creator-content">
          <div className="left-panel">
            {renderForm()}
            
            {/* Create Feature Button */}
            <div className="create-controls" style={{ marginTop: '20px' }}>
              <button
                className="create-button"
                onClick={createFeature}
                disabled={loading || createdFeature}
                style={{
                  padding: '12px 20px',
                  backgroundColor: createdFeature ? '#666' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: createdFeature ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  width: '100%'
                }}
              >
                {loading ? 'Creating...' : createdFeature ? 'Feature Created' : `Create ${activeFeature.charAt(0).toUpperCase() + activeFeature.slice(1)}`}
              </button>
            </div>

            {/* Display Created Feature */}
            {renderCreatedFeature()}
            

            {message && (
              <div className={`message ${message.includes('Error') ? 'error' : 'success'}`} style={{
                marginTop: '20px',
                padding: '10px',
                borderRadius: '5px',
                backgroundColor: message.includes('Error') ? 'rgba(220, 53, 69, 0.1)' : 'rgba(40, 167, 69, 0.1)',
                color: message.includes('Error') ? '#dc3545' : '#28a745',
                border: `1px solid ${message.includes('Error') ? '#dc3545' : '#28a745'}`
              }}>
                {message}
              </div>
            )}
          </div>

        </div>
      </div>
    );
  }

  // Standalone version (if needed)
  return (
    <div className="admin-map-creator-standalone">
      <h1>🏗️ Map Feature Creator</h1>
      {/* Standalone content would go here */}
    </div>
  );
};

export default AdminMapCreator;